import React from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from 'src/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'src/components/ui/collapsible'
import { getIcon } from 'src/lib/navigation-icons'
import { cn } from 'src/lib/utils'
import { toggleSectionCollapse } from 'src/store'

export const NavSection = ({ section, isCollapsed, children }) => {
  const location = useLocation()
  const dispatch = useDispatch()

  // Get section collapse state from Redux
  const sectionState = useSelector((state) => state.ui.sidebarSections)
  const isOpen = sectionState[section.id] ?? section.defaultOpen ?? false

  // Check if any child item is active (for visual highlighting only)
  const hasActiveChild = section.items?.some(item => {
    if (item.type === 'group') {
      return item.items?.some(groupItem => location.pathname === groupItem.to)
    }
    return location.pathname === item.to
  })

  const Icon = getIcon(section.icon)

  // Collapsed sidebar - show icon with popover
  if (isCollapsed) {
    return (
      <div className="relative group">
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-center px-2",
            hasActiveChild
              ? "bg-accent text-accent-foreground"
              : "hover:bg-accent/50"
          )}
          title={section.name}
        >
          {Icon && <Icon className="h-5 w-5" />}
        </Button>
        <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50">
          <div className="bg-popover border rounded-md shadow-lg p-1 min-w-[200px]">
            <div className="font-semibold px-3 py-2 text-sm border-b">{section.name}</div>
            {children}
          </div>
        </div>
      </div>
    )
  }

  // Expanded sidebar - collapsible section
  const handleToggle = () => {
    dispatch(toggleSectionCollapse(section.id))
  }

  return (
    <Collapsible open={isOpen} onOpenChange={handleToggle}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-3 px-4 py-3 h-auto font-semibold text-sm",
            hasActiveChild
              ? "bg-accent text-accent-foreground hover:bg-accent/90"
              : "hover:bg-accent/50"
          )}
        >
          {Icon && <Icon className="h-[18px] w-[18px] shrink-0" />}
          <span className="flex-1 text-left">{section.name}</span>
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-1 pl-3 pt-1 pb-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}
